import styled, { createGlobalStyle } from 'styled-components';

// ─── Print CSS ────────────────────────────────────────────────────────────────

export const PrintStyle = createGlobalStyle`
  @media print {
    body * { visibility: hidden; }
    #unico-result-printable,
    #unico-result-printable * { visibility: visible; }
    #unico-result-printable {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      padding: 24px;
    }
  }
`;

// ─── Layout geral ─────────────────────────────────────────────────────────────

export const PageContainer = styled.div`
  position: relative;
  display: block;
  height: auto;
  overflow-y: visible;
  overflow-x: hidden;
  background: #f1f5f9;
  padding: 28px 24px;
`;

export const PageHeader = styled.div`
  display: block;
  margin-bottom: 20px;
`;

export const PageHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

export const PageHeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const PageTag = styled.p`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #0284c7;
  margin: 0 0 4px;
`;

export const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px;
`;

export const PageSubtitle = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 0;
  max-width: 560px;
`;

// Simulator: phone left | controls right
export const SimulatorLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 20px;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

export const PhoneArea = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 600px;
`;

export const ControlPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const ControlPanelInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const SectionLabel = styled.p`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #94a3b8;
  margin: 0 0 10px;
`;

// ─── Card base ────────────────────────────────────────────────────────────────

export const Card = styled.div`
  display: block;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 24px;
`;

export const CardTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 16px;
`;

// ─── Device Simulator ────────────────────────────────────────────────────────

export const SimulatorSection = styled(Card)``;

export const SimulatorHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 12px;
  flex-wrap: wrap;
`;

export const SimulatorTag = styled.p`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #0284c7;
  margin: 0 0 4px;
`;

export const SimulatorTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
`;

export const OpenCaptureButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  background: #0f172a;
  color: #ffffff;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    background: #1e293b;
  }
`;

export const ScaleModeRow = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
`;

export const ScaleModeButton = styled.button<{ $active: boolean }>`
  padding: 6px 14px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $active }) => ($active ? '#0f172a' : '#f1f5f9')};
  color: ${({ $active }) => ($active ? '#ffffff' : '#475569')};

  &:hover {
    opacity: 0.85;
  }
`;

export const DeviceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
`;

export const DeviceButton = styled.button<{ $active: boolean }>`
  display: block;
  border-radius: 12px;
  border: 1px solid ${({ $active }) => ($active ? '#0284c7' : '#e2e8f0')};
  background: ${({ $active }) => ($active ? '#f0f9ff' : '#ffffff')};
  padding: 8px 10px;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s;

  &:hover {
    border-color: #94a3b8;
  }
`;

export const DeviceButtonName = styled.p`
  font-size: 12px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 2px;
`;

export const DeviceButtonSpec = styled.p`
  font-size: 11px;
  color: #64748b;
  margin: 0;
`;

export const SimulatorBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 260px;
  gap: 20px;
  align-items: flex-start;
  width: 100%;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const PhoneFrame = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
`;

export const PhoneShell = styled.div<{ $width: number; $radius: number }>`
  background: #1e293b;
  border-radius: ${({ $radius }) => $radius + 10}px;
  padding: 10px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35), 0 2px 8px rgba(0, 0, 0, 0.2);
  width: ${({ $width }) => $width + 20}px;
`;

export const PhoneScreen = styled.div<{ $height: number; $width: number; $radius: number }>`
  position: relative;
  border-radius: ${({ $radius }) => $radius}px;
  height: ${({ $height }) => $height}px;
  width: ${({ $width }) => $width}px;
  background: #000;
  overflow: hidden;
  display: block;
`;

export const PhoneNotchIsland = styled.div`
  position: absolute;
  top: 4px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 24px;
  background: rgba(0, 0, 0, 0.9);
  border-radius: 12px;
  z-index: 10;
`;

export const PhoneNotchNotch = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100px;
  height: 24px;
  background: rgba(0, 0, 0, 0.9);
  border-radius: 0 0 14px 14px;
  z-index: 10;
`;

export const PhoneNotchPunch = styled.div`
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: 12px;
  height: 12px;
  background: rgba(0, 0, 0, 0.9);
  border-radius: 50%;
  z-index: 10;
`;

export const DeviceInfoCard = styled(Card)`
  padding: 16px;
  border-left: 3px solid #0284c7;
`;

export const DeviceInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
`;

export const DeviceInfoLabel = styled.span`
  font-size: 12px;
  color: #64748b;
`;

export const DeviceInfoValue = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #0f172a;
`;

// ─── Capture Page ─────────────────────────────────────────────────────────────

export const CaptureContainer = styled.div<{ $embed?: boolean }>`
  display: flex;
  flex-direction: column;
  background: ${({ $embed }) => ($embed ? '#000000' : '#ffffff')};
  overflow: hidden;

  ${({ $embed }) =>
    $embed
      ? `
    width: 100%;
    height: 100%;
  `
      : `
    width: 100%;
    height: 100%;
  `}
`;

export const CaptureTopBar = styled.div`
  padding: 12px 16px;
  background: #0f172a;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

export const CaptureTitle = styled.h1`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
`;

export const ProcessIdBadge = styled.div`
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 8px;
  padding: 4px 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #94a3b8;
  overflow: hidden;

  span {
    color: #38bdf8;
    font-weight: 700;
    font-family: monospace;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 160px;
  }
`;

export const CameraSlot = styled.div`
  flex: 1;
  flex-shrink: 1;
  min-height: 0;
  position: relative;
  width: 100%;
  background: #000;

  #box-camera {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    display: block;
  }
`;

export const CaptureFooter = styled.div`
  padding: 16px;
  background: #ffffff;
  flex-shrink: 0;
`;

export const StatusBadge = styled.div<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 12px;
  background: ${({ $status }) => {
    if ($status === 'success') return '#dcfce7';
    if ($status === 'error') return '#fee2e2';
    if ($status === 'camera_active' || $status === 'processing') return '#fef3c7';
    return '#f1f5f9';
  }};
  color: ${({ $status }) => {
    if ($status === 'success') return '#166534';
    if ($status === 'error') return '#991b1b';
    if ($status === 'camera_active' || $status === 'processing') return '#92400e';
    return '#475569';
  }};
`;

export const CaptureButton = styled.button`
  width: 100%;
  padding: 14px;
  background: #0f172a;
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 8px;

  &:disabled {
    background: #cbd5e1;
    cursor: not-allowed;
  }
`;

export const ResetButton = styled.button`
  width: 100%;
  padding: 10px;
  background: transparent;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }
`;

export const ErrorMessage = styled.p`
  font-size: 13px;
  color: #dc2626;
  margin: 0 0 10px;
`;

export const EmbedControls = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 24px 16px;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.78) 65%, rgba(0, 0, 0, 0.92) 100%);
`;

export const EmbedStatus = styled(StatusBadge)`
  margin-bottom: 0;
`;

export const EmbedCaptureButton = styled(CaptureButton)`
  width: min(280px, 100%);
  margin-bottom: 0;
`;

export const EmbedErrorMessage = styled(ErrorMessage)`
  margin: 0;
  color: #fecaca;
  text-align: center;
`;

// ─── Result Page ──────────────────────────────────────────────────────────────

export const ResultContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 32px 24px;

  @media print {
    padding: 0;
  }
`;

export const ResultHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;

  @media print {
    margin-bottom: 16px;
  }
`;

export const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  background: #dcfce7;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  font-size: 28px;
`;

export const ResultTitle = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 8px;
`;

export const ResultSubtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
`;

export const ResultMainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
  align-items: stretch;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
  @media print {
    grid-template-columns: 1fr 1fr;
  }
`;

export const ResultGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
  align-items: stretch;

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
  }
  @media print {
    grid-template-columns: 1fr 1fr;
  }
`;

export const ResultBottomGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
  align-items: stretch;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
  @media print {
    grid-template-columns: 1fr 1fr;
  }
`;

export const ResultStatusColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const EmptyStateWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
`;

export const EmptyStateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
  max-width: 400px;
  padding: 24px;
`;

export const MockFab = styled.button`
  position: absolute;
  top: 16px;
  right: 20px;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 13px;
  font-size: 12px;
  font-weight: 600;
  color: #7c3aed;
  background: #ede9fe;
  border: 1px solid #c4b5fd;
  border-radius: 20px;
  cursor: pointer;
  transition: background 0.15s, box-shadow 0.15s;
  &:hover {
    background: #ddd6fe;
    box-shadow: 0 2px 8px rgba(124, 58, 237, 0.18);
  }
  @media print {
    display: none;
  }
`;

export const ResultDataColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
`;

export const ResultInlineGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: stretch;
  margin-bottom: 16px;

  > * {
    height: 100%;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px 16px;
`;

export const ResultCard = styled(Card)`
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

export const ResultCardTitle = styled.h4`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #64748b;
  margin: 0 0 12px;
`;

export const SelfieImage = styled.img`
  width: 100%;
  height: 100%;
  min-height: 220px;
  flex: 1;
  object-fit: contain;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #0f172a;
`;

export const SelfiePlaceholder = styled.div`
  flex: 1;
  min-height: 220px;
  background: #f8fafc;
  border: 1px dashed #e2e8f0;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #94a3b8;
`;

export const EncryptedBox = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  font-family: monospace;
  font-size: 11px;
  color: #334155;
  word-break: break-all;
  flex: 1;
  min-height: 0;
  max-height: 180px;
  overflow-y: auto;
  margin-bottom: 10px;
`;

export const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #0f172a;
  cursor: pointer;

  &:hover {
    background: #e2e8f0;
  }
`;

export const TokenBadge = styled.div<{ $expired: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid ${({ $expired }) => ($expired ? '#fca5a5' : '#fcd34d')};
  background: ${({ $expired }) => ($expired ? '#fef2f2' : '#fffbeb')};
  color: ${({ $expired }) => ($expired ? '#991b1b' : '#92400e')};
  font-size: 13px;
  font-weight: 600;
  width: 100%;
  margin-bottom: 8px;
`;

export const TokenDetail = styled.div`
  font-size: 11px;
  color: #94a3b8;
  margin: 4px 0 0;
  line-height: 1.4;
`;

export const ProxyBadge = styled.div<{ $status: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid
    ${({ $status }) => {
      if ($status === 'sent') return '#86efac';
      if ($status === 'failed') return '#fca5a5';
      if ($status === 'skipped_expired') return '#fca5a5';
      return '#bae6fd';
    }};
  background: ${({ $status }) => {
    if ($status === 'sent') return '#f0fdf4';
    if ($status === 'failed' || $status === 'skipped_expired') return '#fef2f2';
    return '#f0f9ff';
  }};
  color: ${({ $status }) => {
    if ($status === 'sent') return '#166534';
    if ($status === 'failed' || $status === 'skipped_expired') return '#991b1b';
    return '#0369a1';
  }};
  font-size: 13px;
  font-weight: 600;
  width: 100%;
  margin-bottom: 8px;
`;

export const JsonBlock = styled.pre`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  font-size: 11px;
  color: #334155;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 260px;
  overflow-y: auto;
  margin: 0;

  @media print {
    max-height: none;
  }
`;

export const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;

  @media print {
    display: none;
  }
`;

export const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: ${({ $variant }) => ($variant === 'primary' ? '#0f172a' : '#f1f5f9')};
  color: ${({ $variant }) => ($variant === 'primary' ? '#ffffff' : '#0f172a')};

  &:hover {
    opacity: 0.85;
  }
`;
